import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@comps/ui/dialog";
import { Textarea } from "@comps/ui/textarea";
import { Item } from "@dto/records";
import { EditRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";
import { getBadgeStyle, getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_TextAreaCell, TW_TextAreaCell_badge, TW_TextAreaCell_editing, TW_TextAreaEditorContent, TW_TextAreaEditorDialogContent, TW_TextCell, TW_TextCell_editing, useHover } from "@utils/twClasses";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataStore, useModelStore, useUserStore } from "src/store";
import { CellProps } from "./GridCell";
import { FieldType } from "@dto/fields";
import { Badge } from "@comps/ui/badge";
import { displayText, samePosition } from "@utils/types";

interface TextAreaCellProps extends CellProps {
    showAsBadge: boolean;
}

export function TextAreaCell({ pathKey, field, item, position, updateValue, isEditing, inputRef, blurTriggered, showAsBadge = false }: TextAreaCellProps) {

    const [text, setText] = useState(item ? item.value || "" : "");
    const [lastBlurTriggered, setLastBlurTriggered] = useState<number | null>(null);
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess || field.type === FieldType.Link;
    const wsColor = useModelStore(state => state.wsColor);
    const badgeStyle = getBadgeStyle(wsColor);
    const edit = useDataStore(state => state.edit);
    const focus = useDataStore(state => state.focus);
    const format = selectedView?.formats?.get(pathKey ?? "") ?? field?.format;
    useEffect(() => {
        if (item?.value !== text) {
            setText(item ? item.value || "" : "");
        }
    }, [item]);

    useEffect(() => {
        const newValue = textToValue(text);
        if (isEditing && lastBlurTriggered && blurTriggered && lastBlurTriggered !== blurTriggered && item?.value !== newValue) {
            updateValue(item, newValue);
        }
        setLastBlurTriggered(blurTriggered);
    }, [blurTriggered]);

    const updateLocalValue = (item: Item, value: any) => {
        setText(value || "");
        updateValue(item, value);
    }

    const textToValue = (text: string | null): any => {
        return text;
    }

    const onTextChange = (text: string) => {
        updateLocalValue(item, text);
        setIsDialogOpen(false);
    }

    function setIsDialogOpen(isOpen: boolean) {
        if (isOpen) {
            const currentFocusPos = useDataStore.getState().focusPos;
            const isSelected = currentFocusPos && currentFocusPos.length === 1 && samePosition(currentFocusPos[0], position);
            if (isSelected && !isEditing) {
                edit(position);
            }
        } else {
            focus(position);
        }
    }

    const displayed = displayText(text, format);
    const splitText = displayed.split("\n");
    let firstLine = splitText[0];
    if (splitText.length > 1) {
        firstLine = firstLine + "...";
    }

    return (
        <Dialog open={isEditing} onOpenChange={setIsDialogOpen}>
            <DialogTrigger className={TW_TextAreaCell}>
                {(showAsBadge && firstLine !== "") ? <Badge className={TW_TextAreaCell_badge} style={badgeStyle}>{firstLine}</Badge> : firstLine}
            </DialogTrigger>
            <DialogContent className={TW_TextAreaEditorDialogContent}>
                <TextAreaEditor initialText={text} onTextChange={onTextChange} onCancel={() => setIsDialogOpen(false)} disabled={readOnly}/>
            </DialogContent>
        </Dialog>
    )
}

interface TextAreaEditorProps {
    initialText: string;
    onTextChange: (text: string) => void;
    onCancel: () => void;
    disabled: boolean;
    // onEnterKeyPress: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    // inputRef: RefObject<HTMLTextAreaElement>;
}

function TextAreaEditor({ initialText, onTextChange, onCancel, disabled }: TextAreaEditorProps) {
    const wsColor = useModelStore(state => state.wsColor);
    const [text, setText] = useState(initialText);
    const { t } = useTranslation();
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const cancelButtonHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
    const saveButtonHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

    const minNumberOfLines = 10;
    const maxNumberOfLines = 20;

    const onSave = () => {
        onTextChange(text);
    }

    const onCancelClick = () => {
        setText(initialText);
        onCancel();
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        event.stopPropagation();
        if (event.key === "Enter" && event.ctrlKey) {
            event.preventDefault();
            onSave();
        }
    }

    const numberOfLines = text.split("\n").length;
    const rows = Math.min(Math.max(numberOfLines, minNumberOfLines), maxNumberOfLines);
    return (
        <div className={TW_TextAreaEditorContent}>
            <Textarea rows={rows} placeholder={t("data.textarea.placeholder")} id="textAreaEditor" className={TW_TextAreaCell_editing} value={text ?? ""} onChange={(event) => setText(event.target.value)} autoFocus={true} style={{ borderColor: wsColor }} onKeyDown={onKeyDown} disabled={disabled} />

            <div className={TW_ButtonList}>
                <Button className={TW_AppDialogButton} onClick={onCancelClick} {...cancelButtonHover}>
                    {disabled ? t("general.button.close.text") : t("general.button.cancel.text")}
                </Button>
                {!disabled && <Button className={TW_AppDialogButton} onClick={onSave} {...saveButtonHover}>{t("data.textarea.save")}</Button>}
            </div>
        </div>
    )
}
